import { useCallback, useEffect, useState } from "react";
import { models } from "jiffy-api";
import * as ActionCable from "@rails/actioncable";
import { objNamesToCamelCase, objNamesToSnakeCase } from "common/utils/helpers/cases";
import { track } from "common/utils/mixpanel/tracker";
import { useExperiments } from "@design-lab/shared/lib/config";

export function useActionCable(uuid: string | undefined, channel: string) {
  const [isConnected, setIsConnected] = useState(false);
  const [socketConnected, setSocketConnected] = useState(() => Promise.withResolvers<void>());
  const [subscription, setSubscription] = useState<ActionCable.Subscription | undefined>(undefined);
  const [lastMessage, setLastMessage] = useState<models.cable.DtfChannel.Actions | undefined>(
    undefined
  );
  const experiments = useExperiments();

  const perform = useCallback(
    (command: models.cable.Perform.Commands) => {
      const dataWithExperiments = { ...command.data, experiments };
      subscription?.perform(command.command, objNamesToSnakeCase(dataWithExperiments));
    },
    [subscription, experiments]
  );

  useEffect(() => {
    if (uuid) {
      const consumer = ActionCable.createConsumer();
      const cableSubscription = consumer.subscriptions.create(
        {
          uuid,
          channel
        },
        {
          connected() {
            setIsConnected(true);
            socketConnected.resolve();
            setSocketConnected(socketConnected);
          },
          disconnected() {
            setIsConnected(false);
            setSocketConnected(Promise.withResolvers());
          },
          received(data) {
            const message = objNamesToCamelCase(data);

            if (message && typeof message === "object" && "action" in message) {
              const eventProperties: Record<string, unknown> = {
                action: message.action,
                channel
              };

              if ("result" in message && message.result && typeof message.result === "object") {
                if ("artworkId" in message.result) {
                  eventProperties["artwork_id"] = message.result["artworkId"];
                }
                if ("success" in message.result) {
                  eventProperties["success"] = message.result["success"];
                }
              }

              track("websocket_message", eventProperties);
              // Skip progress events - don't set them as lastMessage to prevent overwriting important events
              if (message.action === "extract_bg_metadata_progress") {
                return;
              }
            }

            setLastMessage(message);
          }
        }
      );

      setSubscription(cableSubscription);
    }

    return () => {
      subscription?.consumer.disconnect();
      setSubscription(undefined);
    };
  }, [uuid, channel]);

  return {
    isConnected,
    socketConnected: socketConnected.promise,
    lastMessage,
    perform
  };
}
